import { FolderKanban } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { PROJECT_STATUSES, ACTIVE_STATUSES, STATUS_FLOW, TH, TD } from './config';
import type { ProjectRow, ProjectFilters, ProjectSummary } from './hooks';

interface Props {
  projects: ProjectRow[];
  summary: ProjectSummary;
  filters: ProjectFilters;
  setFilters: (f: ProjectFilters) => void;
  onSelect: (p: ProjectRow) => void;
  onAdvance: (id: string, nextStatus: string) => void;
  onNewProject?: () => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }

export function ActiveTab({ projects, summary, filters, setFilters, onSelect, onAdvance, onNewProject }: Props) {
  const active = projects.filter(p => ACTIVE_STATUSES.includes(p.status));

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Active projects" value={String(summary.activeCount)} />
        <MetricCard label="Total budget" value={fmt$(summary.totalBudget)} />
        <MetricCard label="Total spent" value={fmt$(summary.totalSpent)} />
        <MetricCard label="Blocking gates" value={String(summary.blockingGates)}
          delta={summary.blockingGates > 0 ? 'Needs attention' : undefined}
          deltaType={summary.blockingGates > 0 ? 'warn' : undefined} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search projects..." style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 220 }} />
        <select value={filters.status ?? ''} onChange={e => setFilters({ ...filters, status: e.target.value || null })}
          style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
          <option value="">All Statuses</option>
          {ACTIVE_STATUSES.map(s => <option key={s} value={s}>{PROJECT_STATUSES[s]?.label ?? s}</option>)}
        </select>
      </div>

      {/* Table */}
      {active.length === 0 ? (
        <EmptyState icon={<FolderKanban size={22} />} title="No active projects" subtitle="Create a project to manage jobs from start to finish." actionLabel="New Project" onAction={onNewProject} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={TH}>Name</th><th style={TH}>Client</th><th style={TH}>Status</th>
            <th style={TH}>Start Date</th><th style={{ ...TH, textAlign: 'right' }}>Budget</th>
            <th style={TH}>Manager</th><th style={TH}>Action</th>
          </tr></thead>
          <tbody>
            {active.map(p => {
              const st = PROJECT_STATUSES[p.status] ?? PROJECT_STATUSES.lead;
              const nextIdx = STATUS_FLOW.indexOf(p.status) + 1;
              const nextStatus = nextIdx > 0 && nextIdx < STATUS_FLOW.length ? STATUS_FLOW[nextIdx] : null;
              return (
                <tr key={p.id} onClick={() => onSelect(p)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{p.name}</td>
                  <td style={TD}>{p.client_display ?? '—'}</td>
                  <td style={TD}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span>
                  </td>
                  <td style={TD}>{p.start_date ?? p.proposed_start ?? '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{p.budget ? fmt$(p.budget) : '—'}</td>
                  <td style={TD}>{p.manager_name ?? '—'}</td>
                  <td style={TD}>
                    {nextStatus && (
                      <button onClick={e => { e.stopPropagation(); onAdvance(p.id, nextStatus); }} style={{
                        padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: 11, fontWeight: 600,
                        background: palette.accent, color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
                      }}>{PROJECT_STATUSES[nextStatus]?.label ?? nextStatus}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
